import DOMPurify from 'dompurify';
import { Module } from '@/constants/modules';
import { type ValueOf } from '@/types';
import { ModuleType, type StatusModulesEnum } from '@/generated/graphql';
import { DEFAULT_HTML_SANITIZE_OPTIONS } from '@/constants/constants';

export const getModuleIcon = (moduleName: string): string | undefined => {
	const item = Object.values(Module).find((item) => item.name === moduleName);
	return item?.icon;
};

export const getModuleByName = (name: string) => {
	return Object.values(Module).find((item) => item.name === name);
};

export const getTypenameByModule = (moduleName: string): string | undefined => {
	const item = Object.values(Module).find((item) => item.name === moduleName);
	return item?.typename;
};

export const getModuleByTypename = (
	typename: string
): ValueOf<typeof Module> | undefined => {
	return Object.values(Module).find(
		(item) =>
			item.typename?.toLowerCase() === typename.toLowerCase() ||
			item.name.toLowerCase() === typename.toLowerCase()
	);
};

/**
 * Async/await version of `setTimeout`. Useful for waiting for a component to
 * render after making an API request.
 *
 * **Note:** Should not be used in tests with `vi.useFakeTimers()` because it
 * will get stuck indefinitely (waiting for `vi.advanceTimersByTime()` call).
 * `await Vue.nextTick()` can be used in those cases instead.
 *
 * @param ms How many milliseconds to wait for.
 */
export const wait = (ms = 0): Promise<void> =>
	new Promise((resolve) => setTimeout(resolve, ms));

type AllowedAttributesMap = Record<string, string[]>;
type AllowedClassesMap = Record<string, string[]>;

export type SanitizeHTMLOverrides = {
	allowedAttributes?: AllowedAttributesMap;
	allowedClasses?: AllowedClassesMap;
};

const DEFAULT_ALLOWED_CLASSES: AllowedClassesMap = {
	a: ['no-url-formatting', 'text-primary', 'text-underlined'],
};

const UNION_ALLOWED_ATTR = [
	'class',
	'style',
	'href',
	'target',
	'src',
	'alt',
	'title',
	'rel',
];

let activeRestrictions: {
	allowedAttributes: AllowedAttributesMap;
	allowedClasses: AllowedClassesMap;
} | null = null;

DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
	if (!activeRestrictions) {
		return;
	}
	const tag = node.nodeName.toLowerCase();
	const allowedForTag = activeRestrictions.allowedAttributes[tag] ?? [];
	const allowedClassList = activeRestrictions.allowedClasses[tag];
	const classAllowedForTag =
		allowedForTag.includes('class') || allowedClassList !== undefined;

	if (data.attrName === 'class') {
		if (!classAllowedForTag) {
			data.keepAttr = false;
			return;
		}
		const keptClasses = data.attrValue
			.split(/\s+/)
			.filter((c) => c && (allowedClassList ?? []).includes(c));
		if (keptClasses.length === 0) {
			data.keepAttr = false;
		} else {
			data.attrValue = keptClasses.join(' ');
		}
		return;
	}

	if (!allowedForTag.includes(data.attrName)) {
		data.keepAttr = false;
	}
});

/**
 * Sanitize user-provided input
 * Use this method when you *have to* output something `v-html`
 *
 * @param allowedTags Array of allowed tags, e.g., ['b', 'div'].
 */
export const sanitizeHTML = (
	dirtyHtmlString: string,
	allowedTags: string[] = [],
	overrides: SanitizeHTMLOverrides = {}
): string => {
	activeRestrictions = {
		allowedAttributes:
			overrides.allowedAttributes ??
			DEFAULT_HTML_SANITIZE_OPTIONS.allowedAttributes,
		allowedClasses: overrides.allowedClasses ?? DEFAULT_ALLOWED_CLASSES,
	};
	try {
		return DOMPurify.sanitize(dirtyHtmlString, {
			ALLOWED_TAGS: allowedTags,
			ALLOWED_ATTR: UNION_ALLOWED_ATTR,
			KEEP_CONTENT: true,
		});
	} finally {
		activeRestrictions = null;
	}
};

/**
 * Case insensitive search in array of objects
 *
 * @param obj Array of objects to search in
 * @param searchPhrase String to search by
 * @param field Field key of the object to use for search
 */
export const caseInsensitiveSearch = <T extends Record<string, unknown>>(
	obj: T[],
	searchPhrase: string,
	field: keyof T
): T[] => {
	return obj.filter(
		(item) =>
			field in item &&
			String(item[field])
				.toLowerCase()
				.trim()
				.includes(searchPhrase.toLowerCase().trim())
	);
};

const usedUniqueIds: Array<string> = [];

export const getUniqueId = (): string => {
	let id = Math.round(new Date().getTime() * Math.random()).toString(32);

	if (usedUniqueIds.includes(id)) {
		id = getUniqueId();
	}

	usedUniqueIds.push(id);

	return id;
};

/**
 * Remove trailing slash, for example, from URL
 *
 * @param str String to remove trailing slash from
 */
export const removeTrailingSlash = (str: string) => {
	return str.endsWith('/') ? str.slice(0, -1) : str;
};

export const getModuleTypeByStatusModuleEnum = (
	module: StatusModulesEnum
): ModuleType | undefined => {
	const snakeToCamel = (str: string) => {
		return str.toLowerCase().replace(/([-_][a-z])/g, (group: string) => {
			return group.toUpperCase().replace('-', '').replace('_', '');
		});
	};

	const modules = ModuleType;

	for (const [_, value] of Object.entries(modules)) {
		if (value === snakeToCamel(module)) {
			return value;
		}
	}
	return undefined;
};
